/**
 * Modules sagas
 */
import { toast } from "react-toastify";
import { call, put, takeLatest } from "redux-saga/effects";

import { endPoints } from "../../services/endpoints";
import { httpRequest } from "../../services/http-service";
import { dismissLoader, presentLoader } from "../../store/slices/commonSlice";
import {
  addModule,
  clearUploadProgress,
  createModule,
  deleteModule,
  fetchModule,
  fetchModules,
  removeModule,
  setCurrentModule,
  setError,
  setLoading,
  setModules,
  updateModule,
  updateModuleAction,
  uploadModuleVideo,
} from "./modulesSlice";

/**
 * Fetch all modules saga
 */
function* fetchModulesSaga(
  action: ReturnType<typeof fetchModules>
): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const params: { page?: number; limit?: number; isActive?: string } = {
      page: 1,
      limit: 10,
    };

    if (action.payload) {
      if (action.payload.page !== undefined) params.page = action.payload.page;
      if (action.payload.limit !== undefined)
        params.limit = action.payload.limit;
      // Only include isActive parameter when activeOnly is explicitly true
      if (action.payload.activeOnly === true) {
        params.isActive = "true";
      }
      // If activeOnly is false or undefined, don't include isActive in params
    }

    const response = yield call(httpRequest.get, endPoints.modules.getAll, {
      params,
    });

    // Debug: Log response structure
    if (process.env.NODE_ENV === "development") {
      console.log("Modules response:", response);
    }

    if (response?.success) {
      // Backend returns: { success: true, data: [...], pagination: {...} }
      const modulesData = response.data || [];
      const paginationData = response.pagination || {};

      if (Array.isArray(modulesData)) {
        // Pass both data and pagination to the slice
        yield put(
          setModules({
            data: modulesData,
            pagination: paginationData,
          })
        );
      } else {
        // If response.data is not an array, try to extract it
        const data = response.data?.data || response.data || [];
        yield put(
          setModules({
            data: Array.isArray(data) ? data : [],
            pagination: response.pagination || response.data?.pagination || {},
          })
        );
      }
    } else {
      // If response doesn't have success flag, check if it's an array directly
      if (Array.isArray(response)) {
        yield put(setModules(response));
      } else if (response?.data && Array.isArray(response.data)) {
        yield put(
          setModules({
            data: response.data,
            pagination: response.pagination || {},
          })
        );
      } else {
        toast.error("Failed to fetch modules");
        yield put(setError("Failed to fetch modules"));
      }
    }
  } catch (error: any) {
    const message = error?.message || "Failed to fetch modules";
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Fetch single module saga
 */
function* fetchModuleSaga(
  action: ReturnType<typeof fetchModule>
): Generator<any, void, any> {
  try {
    yield put(setLoading(true));

    const response = yield call(
      httpRequest.get,
      endPoints.modules.getById(action.payload)
    );

    if (response?.success) {
      yield put(setCurrentModule(response.data || response));
    } else {
      toast.error("Failed to fetch module");
      yield put(setError("Failed to fetch module"));
    }
  } catch (error: any) {
    const message = error?.message || "Failed to fetch module";
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Create module saga
 */
function* createModuleSaga(
  action: ReturnType<typeof createModule>
): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(
      httpRequest.post,
      endPoints.modules.create,
      action.payload
    );

    if (response?.success) {
      const newModule = response.data || response;
      yield put(addModule(newModule));
    } else {
      toast.error("Failed to create module");
      yield put(setError("Failed to create module"));
    }
  } catch (error: any) {
    const message = error?.message || "Failed to create module";
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Update module saga
 */
function* updateModuleSaga(
  action: ReturnType<typeof updateModuleAction>
): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(
      httpRequest.put,
      endPoints.modules.update(action.payload.id),
      action.payload.data
    );

    if (response?.success) {
      const updatedModule = { ...response.data, _id: action.payload.id };
      yield put(updateModule(updatedModule));
    } else {
      toast.error("Failed to update module");
      yield put(setError("Failed to update module"));
    }
  } catch (error: any) {
    const message = error?.message || "Failed to update module";
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Delete module saga
 */
function* deleteModuleSaga(
  action: ReturnType<typeof deleteModule>
): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(
      httpRequest.delete,
      endPoints.modules.delete(action.payload)
    );

    if (response?.success || response) {
      yield put(removeModule(action.payload));
    } else {
      toast.error("Failed to delete module");
      yield put(setError("Failed to delete module"));
    }
  } catch (error: any) {
    const message = error?.message || "Failed to delete module";
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Upload module video saga
 */
function* uploadModuleVideoSaga(
  action: ReturnType<typeof uploadModuleVideo>
): Generator<any, void, any> {
  try {
    yield put(clearUploadProgress());
    yield put(setLoading(true));

    const formData = new FormData();
    formData.append("video", action.payload.file);

    const response = yield call(
      httpRequest.post,
      endPoints.modules.uploadVideo(action.payload.id),
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent: any) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          // Note: We can't directly dispatch from here in saga, so we'll handle progress in component
        },
      }
    );

    if (response?.success) {
      const updatedModule = response.data || response;
      yield put(updateModule({ ...updatedModule, _id: action.payload.id }));
      yield put(clearUploadProgress());
      toast.success("video is uploaded");
    } else {
      toast.error("Failed to upload video");
      yield put(setError("Failed to upload video"));
      yield put(clearUploadProgress());
    }
  } catch (error: any) {
    const message = error?.message || "Failed to upload video";
    toast.error(message);
    yield put(setError(message));
    yield put(clearUploadProgress());
  } finally {
    yield put(setLoading(false));
  }
}

export function* modulesSagas(): Generator<any, void, any> {
  yield takeLatest(fetchModules, fetchModulesSaga);
  yield takeLatest(fetchModule, fetchModuleSaga);
  yield takeLatest(createModule, createModuleSaga);
  yield takeLatest(updateModuleAction, updateModuleSaga);
  yield takeLatest(deleteModule, deleteModuleSaga);
  yield takeLatest(uploadModuleVideo, uploadModuleVideoSaga);
}
